<template>
  <!-- Shared by the customer and professional panels. The permission gate lives in the PARENT —
       this component assumes it may render whenever it is mounted. -->
  <div class="v2026-ai">

    <div class="v2026-ai__head">
      <span class="v2026-ai__title">ผู้ช่วย Ai</span>
      <button type="button" class="v2026-ai__summarize"
              :disabled="isSummarizing || !selectedConversation"
              @click="getConversationSummary">
        <span class="v2026-ai-mask v2026-ai__summarize-ico" aria-hidden="true"
              :style="maskStyle(icons.sparkles)"></span>
        <span class="v2026-ai__summarize-text">สรุปการสนทนา</span>
      </button>
    </div>

    <div class="v2026-ai-card v2026-ai__result">
      <div class="v2026-ai__loading" v-if="isSummarizing">
        <span class="v2026-ai__spinner" aria-hidden="true"></span>
        <span>กำลังสรุปการสนทนา<span class="v2026-ai__dots"><i></i><i></i><i></i></span></span>
      </div>
      <div class="v2026-ai__bullet v2026-ai__error" v-else-if="aiError">
        {{ aiError }}
      </div>
      <div class="v2026-ai__block" v-else-if="summaryText">
        <div class="v2026-ai__summary-text" v-html="summaryText"></div>
        <div class="v2026-ai__suggest" v-if="category">
          <span v-if="category === 'book_service'">
            <b>AI Suggest:</b>&nbsp;&nbsp;
            <span v-if="isRecommending">กำลังประมวลผล<span class="v2026-ai__dots"><i></i><i></i><i></i></span></span>
            <div v-else v-html="recommendedBookingService"></div>
          </span>
          <span v-else-if="category === 'evaluate_duration'">
            <b>AI Suggest:</b>&nbsp;&nbsp;
            <span v-if="isRecommending">กำลังประมวลผล<span class="v2026-ai__dots"><i></i><i></i><i></i></span></span>
            <span v-else>{{ recommendedDuration }}</span>
          </span>
          <span v-else-if="category === 'big_cleaning'">
            <b>AI Suggest:</b> ประสานงานเจ้าหน้าที่ Big Cleaning
          </span>
        </div>
      </div>
      <div class="v2026-ai__bullet" v-else>{{ emptyText }}</div>
    </div>

    <!-- Result of the chat's "ให้ AI วิเคราะห์รูป" button. Read from the store, never from local data,
         because this component is destroyed on every tab switch. -->
    <div class="v2026-aiv" :class="{ 'v2026-aiv--loading': visionBusy }" v-if="hasVision" ref="visionBlock">

      <div class="v2026-aiv__head">
        <span class="v2026-ai-mask v2026-aiv__head-ico" aria-hidden="true"
              :style="maskStyle(icons.camera)"></span>
        <span class="v2026-aiv__label">วิเคราะห์รูปภาพ</span>
      </div>

      <div class="v2026-ai-card v2026-aiv__card" role="status" aria-live="polite">

        <a class="v2026-aiv__thumb" v-if="visionThumb && !thumbFailed"
           :href="visionThumb" target="_blank" rel="noopener noreferrer" title="เปิดรูปขนาดเต็ม">
          <img :src="visionThumb" alt="รูปที่ส่งให้ AI วิเคราะห์" @error="thumbFailed = true">
        </a>

        <div class="v2026-ai__loading" v-if="visionBusy">
          <span class="v2026-ai__spinner" aria-hidden="true"></span>
          <span>กำลังวิเคราะห์รูป<span class="v2026-ai__dots"><i></i><i></i><i></i></span></span>
        </div>

        <template v-else-if="visionError">
          <div class="v2026-aiv__error" role="alert">ส่งรูปให้ AI ไม่สำเร็จ ยังไม่ได้วิเคราะห์รูปนี้</div>
          <div class="v2026-aiv__meta">กด "ลองใหม่" เพื่อวิเคราะห์รูปเดิมอีกครั้ง</div>
          <div class="v2026-aiv__actions">
            <button type="button" class="v2026-aiv__outline" :disabled="visionLoading" @click="retryVision">
              <span class="v2026-ai-mask v2026-aiv__outline-ico" aria-hidden="true"
                    :style="maskStyle(icons.retry)"></span>
              <span>ลองใหม่</span>
            </button>
          </div>
        </template>

        <template v-else-if="visionShape === 'order'">
          <div class="v2026-aiv__text">รูปนี้ คือ รูปรายละเอียดการจอง <span class="v2026-aiv__strong">{{ visionParams.order_code }}</span></div>
          <a class="v2026-aiv__link" v-if="visionActionUrl"
             :href="visionActionUrl" :title="visionActionUrl" target="_blank" rel="noopener noreferrer">
            <span>เปิดรายละเอียดการจอง</span>
            <span class="v2026-ai-mask v2026-aiv__link-ico" aria-hidden="true"
                  :style="maskStyle(icons.externalLink)"></span>
          </a>
        </template>

        <template v-else-if="visionShape === 'professional'">
          <!-- The server may answer with a direct hit AND a name search; legacy rendered both. -->
          <template v-if="visionActionUrl">
            <div class="v2026-aiv__text">รูปนี้ คือ รูปโปรไฟล์คุณแม่บ้าน</div>
            <a class="v2026-aiv__link"
               :href="visionActionUrl" :title="visionActionUrl" target="_blank" rel="noopener noreferrer">
              <span>เปิดโปรไฟล์แม่บ้าน</span>
              <span class="v2026-ai-mask v2026-aiv__link-ico" aria-hidden="true"
                    :style="maskStyle(icons.externalLink)"></span>
            </a>
          </template>
          <template v-if="visionProfessionals.length">
            <div class="v2026-aiv__text">{{ professionalSearchText }}</div>
            <div class="v2026-aiv__meta">ผลการค้นหา {{ visionProfessionals.length }} รายการ</div>
            <div class="v2026-aiv__list">
              <EntityCardV2026 v-for="(professional, index) in visionProfessionals" :key="index"
                               variant="row"
                               :avatarUrl="professionalAvatar(professional)"
                               :displayName="professionalName(professional)"
                               idLabel="Professional ID:"
                               :idValue="professional.id"
                               :idHref="professionalProfileUrl(professional.id)" />
            </div>
          </template>
        </template>

        <template v-else-if="visionShape === 'slip'">
          <div class="v2026-aiv__text" v-if="slipAmountUsable">รูปนี้ คือ สลิปโอนเงิน ยอด <span class="v2026-aiv__strong">{{ visionParams.amount | prettyNumber }} บาท</span></div>
          <template v-else>
            <div class="v2026-aiv__text">รูปนี้ คือ สลิปโอนเงิน</div>
            <div class="v2026-aiv__meta">ไม่พบยอดเงินในรูป</div>
          </template>
          <template v-if="visionUserId">
            <div class="v2026-aiv__text">หากต้องการใช้สลิปเติมเงินเข้า Wallet สามารถใช้ลิงค์ด้านล่างได้ (โปรดตรวจสอบยอดทุกครั้ง)</div>
            <a class="v2026-aiv__link" v-if="visionActionUrl"
               :href="visionActionUrl" :title="visionActionUrl" target="_blank" rel="noopener noreferrer">
              <span>เปิดหน้าเติมเงิน Wallet</span>
              <span class="v2026-ai-mask v2026-aiv__link-ico" aria-hidden="true"
                    :style="maskStyle(icons.externalLink)"></span>
            </a>
          </template>
        </template>

        <template v-else>
          <div class="v2026-aiv__title-line">
            <span class="v2026-ai-mask v2026-aiv__head-ico" aria-hidden="true"
                  :style="maskStyle(icons.camera)"></span>
            <span>วิเคราะห์เสร็จแล้ว แต่อ่านข้อมูลจากรูปนี้ไม่ได้</span>
          </div>
          <div class="v2026-aiv__text">AI รองรับเฉพาะ สลิปโอนเงิน รายละเอียดการจอง และโปรไฟล์แม่บ้าน</div>
          <div class="v2026-aiv__meta" v-if="visionSubFunction">ประเภทที่ระบบตอบกลับ: {{ visionSubFunction }}</div>
        </template>

      </div>
    </div>

    <AiFunctionCallingV2026 ref="fcBlock" v-if="showFunctionCalling" />

  </div>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'
import EntityCardV2026 from '@/components/coversation/EntityCardV2026.vue'
import AiFunctionCallingV2026 from '@/components/coversation/AiFunctionCallingV2026.vue'

// The vision payload carries a bare filename, unlike panel data which is already a full URL.
const PROFESSIONAL_AVATAR_BASE = 'https://beneat.s3-ap-southeast-1.amazonaws.com/professionals/'
const PLACEHOLDER_AVATAR = 'https://beneat.co/static/img/unknown.png'

// Survives the tab-switch remount, so the block is only scrolled into view once per press.
const REVEAL = { token: null }
const FC_REVEAL = { token: null }

// The tab panes are v-if, so this component is destroyed on every tab switch. One module-level
// slot keeps the last summary alive for the CURRENT conversation: not reactive, not in vuex
// (store.js persists every module to localStorage), gone on reload. `pending` lets a remount
// re-attach to a request that is still in flight instead of showing a dead empty card.
const CACHE = { convId: null, fields: null, pending: null }

const dropCache = (convId) => {
  CACHE.convId = convId
  CACHE.fields = null
  CACHE.pending = null
}

export default {
  name: 'ai-assistant-tab-v2026',
  components: {
    EntityCardV2026,
    AiFunctionCallingV2026
  },
  props: {
    // The parent owns the render gate; this only drives the request.
    selectedConversation: {
      type: Object,
      default: null
    },
    emptyText: {
      type: String,
      default: '• ไม่พบข้อมูลการสนทนาของลูกค้า'
    },
    showFunctionCalling: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      icons: {
        sparkles: require('@/assets/icons2026/ai-magic.svg'),
        camera: require('@/assets/icons2026/composer-camera.svg'),
        retry: require('@/assets/icons2026/refresh.svg'),
        externalLink: require('@/assets/icons2026/external-link.svg')
      },
      isSummarizing: false, // summary GET in flight → loading indicator
      isRecommending: false, // AI-Suggest POST in flight
      summaryText: null, // single HTML blob (v-html); null = empty state
      category: null, // drives the AI-Suggest branch
      recommendedDuration: null, // evaluate_duration result
      recommendedBookingService: null, // book_service result (HTML)
      isNewCustomer: false, // derived from selectedConversation.customer.beneat_user_id
      activeConversationId: 0, // reset guard (same pattern as original watch)
      aiError: null, // minimal error fallback (ADDITION — original has none)
      thumbFailed: false // the ONLY vision state allowed to live here; everything else is in vuex
    }
  },
  computed: {
    ...mapState('aiAssistant', ['visionRequest', 'visionLoading', 'visionResult', 'visionError', 'fcRequest']),
    hasVision () {
      return !!(this.visionLoading || this.visionResult || this.visionError || this.visionRequest)
    },
    // Also covers the torn state (a request written, no result yet) so the card is never blank.
    visionBusy () {
      return !!this.visionLoading || (!this.visionResult && !this.visionError)
    },
    visionThumb () {
      const fromResult = this.visionResult && this.visionResult.photo_url
      const fromRequest = this.visionRequest && this.visionRequest.photoUrl
      return fromResult || fromRequest || null
    },
    visionParams () {
      return (this.visionResult && this.visionResult.params) || {}
    },
    visionActionUrl () {
      return (this.visionResult && this.visionResult.action_url) || null
    },
    visionUserId () {
      return (this.visionResult && this.visionResult.user_id) || null
    },
    visionSubFunction () {
      return (this.visionResult && this.visionResult.sub_function_name) || null
    },
    visionProfessionals () {
      const list = this.visionResult && this.visionResult.professionals
      return Array.isArray(list) ? list : []
    },
    // A known name with an unusable payload falls through to 'unknown' rather than an empty box.
    visionShape () {
      if (!this.visionResult) return 'unknown'
      if (this.visionSubFunction === 'get_order_code') {
        return this.visionParams.order_code ? 'order' : 'unknown'
      }
      if (this.visionSubFunction === 'get_professional_id') {
        return (this.visionActionUrl || this.visionProfessionals.length) ? 'professional' : 'unknown'
      }
      if (this.visionSubFunction === 'get_bank_transfer_slip') return 'slip'
      return 'unknown'
    },
    slipAmountUsable () {
      return Number.isFinite(Number(this.visionParams.amount))
    },
    professionalSearchText () {
      const name = (this.visionParams.name || '').toString().trim()
      return name
        ? `รูปนี้ คือ รูปโปรไฟล์คุณแม่บ้าน แต่ไม่มีการระบุ id จึงใช้การค้นหาจากชื่อ ${name} แทน ได้ผลลัพธ์ดังนี้`
        : 'รูปนี้ คือ รูปโปรไฟล์คุณแม่บ้าน แต่ไม่มีการระบุ id จึงใช้การค้นหาจากชื่อในรูปแทน ได้ผลลัพธ์ดังนี้'
    }
  },
  watch: {
    // Watches the id only, and calls no endpoint — otherwise summarising would retrigger the watch.
    selectedConversation () {
      if (this.selectedConversation && this.selectedConversation.id !== this.activeConversationId) {
        this.activeConversationId = this.selectedConversation.id
        this.summaryText = null
        this.category = null
        this.recommendedDuration = null
        this.recommendedBookingService = null
        this.aiError = null
        this.isSummarizing = false
        this.isRecommending = false
        this.isNewCustomer = !(this.selectedConversation.customer && this.selectedConversation.customer.beneat_user_id)
        dropCache(this.activeConversationId)
      }
    },
    visionThumb () {
      this.thumbFailed = false
    },
    visionRequest () {
      this.revealVision()
    },
    fcRequest () {
      this.revealFunctionCalling()
    }
  },
  methods: {
    // Own helper: the tab is shared, and CustomerPanelV2026 has no linkProfileProfessional.
    professionalProfileUrl (id) {
      return id != null ? `https://admin-test.beneat.co/professional/${id}/profile` : null
    },
    professionalAvatar (professional) {
      return professional && professional.picture
        ? `${PROFESSIONAL_AVATAR_BASE}${professional.picture}`
        : PLACEHOLDER_AVATAR
    },
    professionalName (professional) {
      const name = `${(professional && professional.firstname) || ''} ${(professional && professional.lastname) || ''}`.trim()
      return name || '-'
    },
    // Same action, same stored payload — no second call path to the vision API.
    retryVision () {
      if (this.visionLoading) return
      const request = this.visionRequest
      if (!request || !request.submitData) return
      this.$store.dispatch('aiAssistant/callAiVision', request.submitData)
    },
    // One nudge per press; 'nearest' is a no-op when the block is already visible.
    revealVision () {
      const token = this.visionRequest ? this.visionRequest.token : null
      if (token === null || token === REVEAL.token) return
      REVEAL.token = token
      this.$nextTick(() => {
        const el = this.$refs.visionBlock
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' })
      })
    },
    revealFunctionCalling () {
      const token = this.fcRequest ? this.fcRequest.token : null
      if (token === null || token === FC_REVEAL.token) return
      FC_REVEAL.token = token
      this.$nextTick(() => {
        // $el is a COMMENT node while the block is empty, hence the capability check.
        const component = this.$refs.fcBlock
        const el = component && component.$el
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' })
      })
    },
    maskStyle (url) {
      return {
        '-webkit-mask-image': 'url(' + url + ')',
        'mask-image': 'url(' + url + ')'
      }
    },
    // Every result goes through here so it lands in the cache too. The id is passed in, not read
    // off `this`: a request for the previous conversation must not write into the new one's slot.
    cacheWrite (convId, patch) {
      if (this.activeConversationId === convId) Object.assign(this, patch)
      if (CACHE.convId !== convId) return
      CACHE.fields = Object.assign({}, CACHE.fields, patch)
    },
    setBusy (convId, patch) {
      if (this.activeConversationId === convId) Object.assign(this, patch)
    },
    getConversationSummary () {
      if (!this.selectedConversation) return
      const id = this.selectedConversation.id
      dropCache(id)
      this.isSummarizing = true
      this.isRecommending = false
      const req = this.runSummary(id)
      CACHE.pending = req
      req.then(() => {
        if (CACHE.pending === req) CACHE.pending = null
      })
    },
    async runSummary (id) {
      this.cacheWrite(id, {
        summaryText: null,
        category: null,
        recommendedDuration: null,
        recommendedBookingService: null,
        aiError: null
      })
      let category = null
      let inputText = null
      try {
        const url = `/ai-assistant/conversation-summary/${id}`
        const { data } = await axios.get(url)
        if (data) {
          category = data.category
          inputText = data.inputText
          this.cacheWrite(id, { summaryText: data.summaryText, category: data.category })
        }
      } catch (e) {
        this.cacheWrite(id, { aiError: 'เกิดข้อผิดพลาด ลองใหม่' })
      }
      // Cleared before the AI-Suggest call on purpose: the suggestion has its own inline spinner,
      // so the summary shows as soon as it arrives rather than waiting for the follow-up.
      this.setBusy(id, { isSummarizing: false })
      if (category === 'book_service') await this.recommendBookingService(id, inputText)
      else if (category === 'evaluate_duration') await this.recommendDuration(id, inputText)
    },
    async recommendDuration (id, inputText) {
      this.setBusy(id, { isRecommending: true })
      this.cacheWrite(id, { recommendedDuration: null })
      try {
        const url = `/ai-assistant/recommend-duration`
        const { data } = await axios.post(url, { inputText })
        if (data) {
          this.cacheWrite(id, { recommendedDuration: data.result })
        }
      } catch (e) {
        this.cacheWrite(id, { aiError: 'เกิดข้อผิดพลาด ลองใหม่' })
      }
      this.setBusy(id, { isRecommending: false })
    },
    async recommendBookingService (id, inputText) {
      this.setBusy(id, { isRecommending: true })
      this.cacheWrite(id, { recommendedBookingService: null })
      try {
        const url = `/ai-assistant/recommend-booking-service`
        const { data } = await axios.post(url, {
          inputText: inputText,
          isNewCustomer: this.isNewCustomer
        })
        if (data) {
          this.cacheWrite(id, { recommendedBookingService: data.result.replace(/(\d\.)/g, '<br>$1 ') })
        }
      } catch (e) {
        this.cacheWrite(id, { aiError: 'เกิดข้อผิดพลาด ลองใหม่' })
      }
      this.setBusy(id, { isRecommending: false })
    }
  },
  // The watch only fires on change, so a conversation already selected at first render is seeded here.
  mounted () {
    this.revealVision()
    this.revealFunctionCalling()
    if (!this.selectedConversation) return
    this.activeConversationId = this.selectedConversation.id
    this.isNewCustomer = !(this.selectedConversation.customer && this.selectedConversation.customer.beneat_user_id)
    if (CACHE.convId !== this.activeConversationId) {
      dropCache(this.activeConversationId)
      return
    }
    if (CACHE.fields) Object.assign(this, CACHE.fields)
    const pending = CACHE.pending
    if (!pending) return
    // Re-attached mid-request: the summary having already landed is what tells the two spinners
    // apart, since only the AI-Suggest leg can still be running once it has.
    this.isSummarizing = !this.summaryText
    this.isRecommending = !!this.summaryText
    pending.then(() => {
      if (CACHE.fields) Object.assign(this, CACHE.fields)
      this.isSummarizing = false
      this.isRecommending = false
    })
  }
}
</script>

<style scoped>
.v2026-ai-mask {
  display: inline-block;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
  background-color: var(--v2026-icon);
}

.v2026-ai-card {
  background: var(--v2026-surface-sunken);
  border-radius: var(--v2026-r-card);
}

.v2026-ai {
  display: flex;
  flex-direction: column;
  padding: var(--v2026-sp-4) var(--v2026-sp-4) 0;
  gap: var(--v2026-sp-3);
}
.v2026-ai__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--v2026-sp-3);
}
.v2026-ai__title {
  font-size: var(--v2026-fs-name);
  font-weight: 700;
  color: var(--v2026-text);
}

.v2026-ai__summarize {
  display: inline-flex;
  align-items: center;
  gap: var(--v2026-sp-2);
  padding: var(--v2026-sp-2) var(--v2026-sp-3);
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  background: linear-gradient(135deg, #2f6bff 0%, #7c3aed 100%);
  color: #ffffff;
  font-size: var(--v2026-fs-body);
  font-weight: 600;
}
.v2026-ai__summarize:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.30);
}

.v2026-ai__summarize-ico {
  width: 16px;
  height: 16px;
  background-color: #ffffff;
}
.v2026-ai__summarize-text {
  white-space: nowrap;
}

.v2026-ai__summarize:disabled {
  opacity: 0.6;
  cursor: default;
}

.v2026-ai__result {
  display: flex;
  flex-direction: column;
  gap: var(--v2026-sp-3);
  padding: var(--v2026-sp-3) var(--v2026-sp-4);
}
.v2026-ai__block {
  display: flex;
  flex-direction: column;
  gap: var(--v2026-sp-1);
}
.v2026-ai__bullet {
  font-size: var(--v2026-fs-body);
  color: var(--v2026-text-secondary);
  line-height: 1.5;
  word-break: break-word;
}

.v2026-ai__summary-text {
  font-size: var(--v2026-fs-body);
  color: var(--v2026-text);
  line-height: 1.5;
  word-break: break-word;
  white-space: pre-line;
}

.v2026-ai__suggest {
  margin-top: var(--v2026-sp-3);
  font-size: var(--v2026-fs-body);
  color: var(--v2026-text-secondary);
  line-height: 1.5;
  word-break: break-word;
}
.v2026-ai__suggest b {
  color: var(--v2026-text);
}

.v2026-ai__loading {
  color: var(--v2026-text-muted);
  display: flex;
  align-items: center;
  gap: 8px;
}

.v2026-ai__spinner {
  width: 14px;
  height: 14px;
  flex: 0 0 auto;
  border: 2px solid rgba(124, 58, 237, 0.25);
  border-top-color: #7c3aed;
  border-radius: 50%;
  animation: v2026-ai-spin 0.7s linear infinite;
}
@keyframes v2026-ai-spin {
  to { transform: rotate(360deg); }
}

.v2026-ai__dots i {
  display: inline-block;
  width: 3px;
  height: 3px;
  margin-left: 2px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.3;
  animation: v2026-ai-dot 1.2s infinite;
}
.v2026-ai__dots i:nth-child(2) { animation-delay: 0.2s; }
.v2026-ai__dots i:nth-child(3) { animation-delay: 0.4s; }
@keyframes v2026-ai-dot {
  0%, 60%, 100% { opacity: 0.3; }
  30% { opacity: 1; }
}
.v2026-ai__error {
  color: #f04438;
}

.v2026-aiv {
  display: flex;
  flex-direction: column;
  gap: var(--v2026-sp-2);
}
.v2026-aiv__head {
  display: flex;
  align-items: center;
  gap: var(--v2026-sp-2);
}
.v2026-aiv__head-ico {
  width: 14px;
  height: 14px;
  flex: 0 0 auto;
}
.v2026-aiv__label {
  font-size: var(--v2026-fs-section);
  font-weight: 600;
  color: var(--v2026-text-secondary);
}

.v2026-aiv__card {
  display: flex;
  flex-direction: column;
  gap: var(--v2026-sp-3);
  padding: var(--v2026-sp-3) var(--v2026-sp-4);
}

.v2026-aiv__thumb {
  display: block;
  align-self: flex-start;
  max-width: 100%;
}
.v2026-aiv__thumb img {
  display: block;
  height: 88px;
  width: auto;
  max-width: 100%;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid var(--v2026-border);
}
.v2026-aiv__thumb:hover img {
  opacity: 0.9;
  cursor: zoom-in;
}
.v2026-aiv__thumb:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
  border-radius: 8px;
}
.v2026-aiv--loading .v2026-aiv__thumb img {
  opacity: 0.65;
}

.v2026-aiv__text {
  font-size: var(--v2026-fs-body);
  color: var(--v2026-text-secondary);
  line-height: 1.5;
  word-break: break-word;
}
.v2026-aiv__strong {
  color: var(--v2026-text);
  font-weight: 600;
}
.v2026-aiv__meta {
  font-size: var(--v2026-fs-meta);
  color: var(--v2026-text-muted);
  line-height: 1.5;
  word-break: break-word;
}

.v2026-aiv__title-line {
  display: flex;
  align-items: center;
  gap: var(--v2026-sp-2);
  font-size: var(--v2026-fs-body);
  font-weight: 600;
  color: var(--v2026-text-secondary);
  line-height: 1.5;
  word-break: break-word;
}

/* Own red instead of .v2026-ai__error: #f04438 on the sunken surface misses AA for body text. */
.v2026-aiv__error {
  font-size: var(--v2026-fs-body);
  font-weight: 600;
  color: #B42318;
  line-height: 1.5;
  word-break: break-word;
}

.v2026-aiv__link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--v2026-sp-2);
  font-size: var(--v2026-fs-body);
  color: var(--v2026-link);
  line-height: 1.5;
  word-break: break-word;
}
.v2026-aiv__link:hover {
  text-decoration: underline;
}
.v2026-aiv__link:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
  border-radius: 4px;
}
.v2026-aiv__link-ico {
  width: 14px;
  height: 14px;
  flex: 0 0 auto;
  background-color: currentColor;
}

.v2026-aiv__list {
  background: var(--v2026-surface);
  border-radius: var(--v2026-r-card);
  overflow: hidden;
}

.v2026-aiv__actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--v2026-sp-2);
}
.v2026-aiv__outline {
  display: inline-flex;
  align-items: center;
  gap: var(--v2026-sp-2);
  padding: var(--v2026-sp-2) var(--v2026-sp-3);
  border: 1px solid var(--v2026-border);
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  font-size: var(--v2026-fs-body);
  color: var(--v2026-text-secondary);
}
.v2026-aiv__outline:hover {
  background: var(--v2026-surface-hover);
}
.v2026-aiv__outline:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}
.v2026-aiv__outline:disabled {
  opacity: 0.6;
  cursor: default;
}
.v2026-aiv__outline-ico {
  width: 14px;
  height: 14px;
  background-color: currentColor;
}
</style>
